import React from 'react';
import { Paper, Box, Typography, Chip, Link } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

/** Matches Sales order / work order summary strips */
export const procurementSummaryPaperSx = {
  p: 1.5,
  mb: 1.5,
  backgroundColor: '#f5f5f5',
  border: '1px solid #e0e0e0'
};

export const procurementFieldCaptionSx = {
  fontSize: '0.7rem',
  color: 'text.secondary',
  display: 'block'
};

export function ProcurementSummaryBar({ children, dense }) {
  return (
    <Paper
      sx={{
        ...procurementSummaryPaperSx,
        ...(dense ? { p: 1, mb: 1 } : {}),
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: dense ? 1 : 2,
          ...(dense ? { rowGap: 0.5 } : {}),
        }}
      >
        {children}
      </Box>
    </Paper>
  );
}

export function ProcurementSummaryField({ label, children, wide, dense }) {
  return (
    <Box sx={wide ? { flexGrow: 1, minWidth: dense ? 160 : 200 } : {}}>
      <Typography
        variant="caption"
        sx={{
          ...procurementFieldCaptionSx,
          ...(dense ? { fontSize: '0.65rem', lineHeight: 1.2, mb: 0 } : {}),
        }}
      >
        {label}
      </Typography>
      <Typography
        variant="body2"
        component="div"
        sx={dense ? { fontSize: '0.8125rem', lineHeight: 1.3, mt: 0.1 } : {}}
      >
        {children}
      </Typography>
    </Box>
  );
}

const PO_STATUS_ORDERED = 'ORDERED';
const PO_STATUS_PARTIAL = 'PARTIALLY RCVD';
const PO_STATUS_FULL = 'RECEIVED IN FULL';
const PO_STATUS_CANCELLED = 'CANCELLED';

export function getPoReceiptStatusChipProps(status) {
  const s = (status || '').toString().trim().toUpperCase();
  if (s === PO_STATUS_CANCELLED) return { color: 'error', label: 'CANCELLED' };
  if (s === PO_STATUS_PARTIAL) return { color: 'warning', label: 'PARTIALLY RCVD' };
  if (s === PO_STATUS_FULL) return { color: 'success', label: 'RECEIVED IN FULL' };
  return { color: 'info', label: PO_STATUS_ORDERED };
}

export function PoReceiptStatusChip({ status, size = 'small' }) {
  const { color, label } = getPoReceiptStatusChipProps(status);
  return <Chip label={label} color={color} size={size} sx={{ fontWeight: 500 }} />;
}

/** Shipment received: customer/vendor P.O. on paperwork; UNKNOWN PO when not tied to a PO and blank */
export function formatShipmentPoDisplay(row) {
  if (!row) return '—';
  if (row.purchase_order_id) return row.po_number || '—';
  const p = row.po_number != null ? String(row.po_number).trim() : '';
  return p || 'UNKNOWN PO';
}

/** Purchase order linked to this shipment (system identifier / number). */
export function formatLinkedPoDisplay(row) {
  if (!row) return '—';
  if (!row.purchase_order_id) return '—';
  const id = row.purchase_order_identifier != null ? String(row.purchase_order_identifier).trim() : '';
  if (id) return id;
  const num = row.po_number != null ? String(row.po_number).trim() : '';
  if (num) return num;
  return `PO #${row.purchase_order_id}`;
}

/** Linked shipment-in records for a PO summary strip (comma-separated links). */
export function ShipmentsReceivedSummaryLinks({ receivers }) {
  if (!receivers?.length) return '—';
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.5, rowGap: 0.25 }}>
      {receivers.map((r, i) => (
        <Box key={r.id} component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
          {i > 0 ? (
            <Typography component="span" variant="body2" color="text.secondary" sx={{ mr: 0.5 }}>
              ,
            </Typography>
          ) : null}
          <Link component={RouterLink} to={`/procurement/shipments-in/${r.id}`} variant="body2" underline="hover">
            {r.identifier ?? r.receiver_no ?? `#${r.id}`}
          </Link>
        </Box>
      ))}
    </Box>
  );
}
